import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Grid,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  LinearProgress,
  Tooltip,
  Pagination,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Image as ImageIcon,
  ZoomIn as ZoomInIcon,
  PlayArrow as PlayIcon,
  VideoFile as VideoIcon,
  CheckCircle,
  Error,
  HourglassEmpty,
  Sync,
  Add as AddIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material';
import { ImageService, ImageMetadata } from '../../services/imageService';
import { ConfirmDialog } from './common';
import { useSnackbar } from '../../hooks/useSnackbar';

interface ImageLibraryTabProps {
  siteId: string;
  editState?: any;
  updateEditState?: (updates: any) => void;
  cachedImages?: ImageMetadata[];
  onImagesChange?: (images: ImageMetadata[]) => void;
  preloaded?: boolean;
}

export const ImageLibraryTab: React.FC<ImageLibraryTabProps> = ({ 
  siteId, 
  editState, 
  updateEditState,
  cachedImages = [],
  onImagesChange,
  preloaded = false
}) => {
  const [images, setImages] = useState<ImageMetadata[]>(cachedImages);
  const [loading, setLoading] = useState(!preloaded);
  const [uploading, setUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState<ImageMetadata | null>(null);
  const [deletingImage, setDeletingImage] = useState<ImageMetadata | null>(null);
  const [editingImage, setEditingImage] = useState<ImageMetadata | null>(null);
  const [editImageName, setEditImageName] = useState<string>('');
  const [duplicateFileDialog, setDuplicateFileDialog] = useState<{
    open: boolean;
    file: File | null;
    fileName: string;
    isVideo: boolean;
  }>({
    open: false,
    file: null,
    fileName: '',
    isVideo: false,
  });
  const [pendingUploads, setPendingUploads] = useState<Array<{
    file: File;
    fileName: string;
    isVideo: boolean;
  }>>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState<'images' | 'videos'>('images');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filterType, setFilterType] = useState<'all' | 'used' | 'unused'>('all');
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const { snackbar, showSnackbar, handleClose: handleSnackbarClose } = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 画像一覧を読み込む
  const loadImages = async () => {
    setLoading(true);
    try {
      const imageList = await ImageService.getSiteImageLibrary(siteId);
      setImages(imageList);
      if (onImagesChange) {
        onImagesChange(imageList);
      }
    } catch (error) {
      console.error('画像の読み込みに失敗しました:', error);
      showSnackbar('画像の読み込みに失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // 事前読み込み済みの場合はキャッシュを使用、そうでなければ読み込む
    if (preloaded && cachedImages.length > 0) {
      setImages(cachedImages);
      setLoading(false);
    } else if (siteId && !preloaded) {
      loadImages();
    }
  }, [siteId, preloaded, cachedImages]);

  // 実際のアップロード処理
  const processUpload = async (
    file: File, 
    fileName: string, 
    isVideo: boolean, 
    overwrite: boolean = false,
    autoNumber: boolean = false
  ): Promise<ImageMetadata | null> => {
    try {
      const mediaUrl = await ImageService.uploadMedia(file, siteId, undefined, {
        fileName,
        overwrite,
        autoNumber,
      });
      
      // ファイル拡張子とcontentTypeを決定
      let contentType: string;
      if (isVideo) {
        contentType = 'video/mp4';
      } else if (file.type === 'image/gif') {
        contentType = 'image/gif';
      } else {
        contentType = 'image/webp';
      }
      
      // ストレージパスを構築
      const mediaType = isVideo ? 'videos' : 'images';
      const fullPath = `sites/${siteId}/library/${mediaType}/${fileName}`;
      
      return {
        url: mediaUrl,
        name: fileName,
        size: file.size,
        uploadedAt: new Date(),
        fullPath,
        contentType,
        isVideo,
      };
    } catch (error) {
      console.error('アップロードエラー:', error);
      throw error;
    }
  };

  // ファイル選択
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const filesToProcess: Array<{ file: File; fileName: string; isVideo: boolean }> = [];
    
    // 各ファイルのチェックとファイル名決定
    for (const file of files) {
      // ファイルタイプチェック
      if (!ImageService.isSupportedMediaType(file)) {
        showSnackbar(`${file.name} はサポートされていない形式です`, 'error');
        continue;
      }

      // ファイルサイズチェック
      if (!ImageService.isValidFileSize(file, 100)) {
        showSnackbar(`${file.name} のサイズが大きすぎます（最大100MB）`, 'error');
        continue;
      }

      const isVideo = ImageService.isSupportedVideoType(file);
      
      // ファイル拡張子を決定
      let fileExtension: string;
      if (isVideo) {
        fileExtension = 'mp4';
      } else if (file.type === 'image/gif') {
        fileExtension = 'gif';
      } else {
        fileExtension = 'webp';
      }
      
      const originalFileName = file.name.replace(/\.[^/.]+$/, `.${fileExtension}`);
      
      // 同名ファイルのチェック
      const fileExists = await ImageService.fileExists(originalFileName, siteId, isVideo);
      
      if (fileExists) {
        // 同名ファイルがある場合はモーダルを表示
        setDuplicateFileDialog({
          open: true,
          file,
          fileName: originalFileName,
          isVideo,
        });
        // 最初の同名ファイルで一旦停止（後で改善）
        break;
      } else {
        // 同名ファイルがない場合はそのまま追加
        filesToProcess.push({ file, fileName: originalFileName, isVideo });
      }
    }
    
    // 同名ファイルがないファイルを処理
    if (filesToProcess.length > 0) {
      await processFiles(filesToProcess);
    }
    
    // ファイル入力をリセット
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // ファイルアップロード処理
  const processFiles = async (filesToProcess: Array<{ file: File; fileName: string; isVideo: boolean }>) => {
    setUploading(true);
    const uploadedImages: ImageMetadata[] = [];
    
    try {
      for (const { file, fileName, isVideo } of filesToProcess) {
        // 動画形式の変換通知
        if (isVideo && !['video/mp4', 'video/webm', 'video/ogg'].includes(file.type)) {
          showSnackbar(`${file.name} をMP4形式に変換中...`, 'success');
        }

        const metadata = await processUpload(file, fileName, isVideo, false, false);
        if (metadata) {
          uploadedImages.push(metadata);
        }
      }

      if (uploadedImages.length > 0) {
        // 再読み込みせずに状態の先頭に追加
        const updatedImages = [...uploadedImages, ...images];
        setImages(updatedImages);
        if (onImagesChange) {
          onImagesChange(updatedImages);
        }
        const imageCount = uploadedImages.filter(img => !img.isVideo).length;
        const videoCount = uploadedImages.filter(img => img.isVideo).length;
        let message = '';
        if (imageCount > 0 && videoCount > 0) {
          message = `${imageCount}件の画像と${videoCount}件の動画をアップロードしました`;
        } else if (imageCount > 0) {
          message = `${imageCount}件の画像をアップロードしました`;
        } else if (videoCount > 0) {
          message = `${videoCount}件の動画をアップロードしました`;
        }
        showSnackbar(message, 'success');
      }
    } catch (error) {
      console.error('アップロードエラー:', error);
      showSnackbar('アップロードに失敗しました', 'error');
    } finally {
      setUploading(false);
    }
  };

  // 同名ファイル処理（上書き）
  const handleDuplicateOverwrite = async () => {
    if (!duplicateFileDialog.file) return;
    
    const { file, fileName, isVideo } = duplicateFileDialog;
    setDuplicateFileDialog({ open: false, file: null, fileName: '', isVideo: false });
    
    setUploading(true);
    try {
      const metadata = await processUpload(file, fileName, isVideo, true, false);
      if (metadata) {
        const updatedImages = [metadata, ...images];
        setImages(updatedImages);
        if (onImagesChange) {
          onImagesChange(updatedImages);
        }
        showSnackbar('ファイルをアップロードしました', 'success');
      }
    } catch (error) {
      console.error('アップロードエラー:', error);
      showSnackbar('アップロードに失敗しました', 'error');
    } finally {
      setUploading(false);
      // ファイル入力をリセット
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // 同名ファイル処理（採番）
  const handleDuplicateAutoNumber = async () => {
    if (!duplicateFileDialog.file) return;
    
    const { file, fileName, isVideo } = duplicateFileDialog;
    setDuplicateFileDialog({ open: false, file: null, fileName: '', isVideo: false });
    
    setUploading(true);
    try {
      const availableFileName = await ImageService.getAvailableFileName(fileName, siteId, isVideo);
      const metadata = await processUpload(file, availableFileName, isVideo, false, false);
      if (metadata) {
        const updatedImages = [metadata, ...images];
        setImages(updatedImages);
        if (onImagesChange) {
          onImagesChange(updatedImages);
        }
        showSnackbar('ファイルをアップロードしました', 'success');
      }
    } catch (error) {
      console.error('アップロードエラー:', error);
      showSnackbar('アップロードに失敗しました', 'error');
    } finally {
      setUploading(false);
      // ファイル入力をリセット
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // URLをクリップボードにコピー
  const handleCopyUrl = (url: string, name: string) => {
    navigator.clipboard.writeText(url).then(
      () => {
        showSnackbar(`${name} のURLをコピーしました`, 'success');
      },
      (err) => {
        console.error('コピーに失敗しました:', err);
        showSnackbar('URLのコピーに失敗しました', 'error');
      }
    );
  };

  // ファイル名から拡張子を取得
  const getFileExtension = (fileName: string): string => {
    const lastDotIndex = fileName.lastIndexOf('.');
    return lastDotIndex > 0 ? fileName.substring(lastDotIndex) : '';
  };

  // ファイル名から拡張子を除いた部分を取得
  const getFileNameWithoutExtension = (fileName: string): string => {
    const lastDotIndex = fileName.lastIndexOf('.');
    return lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;
  };

  // 編集開始（インライン編集）
  const handleEditStart = (image: ImageMetadata) => {
    setEditingImage(image);
    // 拡張子を除いたファイル名のみを設定
    setEditImageName(getFileNameWithoutExtension(image.name));
  };

  // 編集キャンセル
  const handleEditCancel = () => {
    setEditingImage(null);
    setEditImageName('');
  };

  // 画像名を変更
  const handleRenameConfirm = async () => {
    if (!editingImage || !editImageName.trim()) return;

    try {
      // 元の拡張子を保持
      const originalExtension = getFileExtension(editingImage.name);
      const newFileName = `${editImageName.trim()}${originalExtension}`;
      
      const newUrl = await ImageService.renameFile(
        editingImage.fullPath,
        newFileName,
        siteId
      );
      
      // 新しいfullPathを構築
      const mediaType = editingImage.isVideo ? 'videos' : 'images';
      const newFullPath = `sites/${siteId}/library/${mediaType}/${newFileName}`;
      
      // 画像一覧を更新
      const updatedImages = images.map(img => 
        img.url === editingImage.url 
          ? { ...img, name: newFileName, url: newUrl, fullPath: newFullPath }
          : img
      );
      setImages(updatedImages);
      if (onImagesChange) {
        onImagesChange(updatedImages);
      }
      
      // この画像を使用している設定のURLも更新
      if (updateEditState) {
        const updates: any = {};
        let hasUpdates = false;
        const oldUrl = editingImage.url;
        
        // ページの背景画像、スロット画像、横スライド画像をチェック
        if (editState?.pages && Array.isArray(editState.pages)) {
          const updatedPages = editState.pages.map((page: any) => {
            let pageUpdated = false;
            const updatedPage = { ...page };
            
            // ページの背景画像
            if (page.backgroundImage === oldUrl) {
              updatedPage.backgroundImage = newUrl;
              pageUpdated = true;
            }
            
            // ページスロットの画像
            if (page.slots && Array.isArray(page.slots)) {
              updatedPage.slots = page.slots.map((slot: any) => {
                let slotUpdated = false;
                const updatedSlot = { ...slot };
                
                if (slot.imageUrl === oldUrl) {
                  updatedSlot.imageUrl = newUrl;
                  slotUpdated = true;
                }
                
                // スロット内のページオブジェクトの背景画像
                if (slot.page && slot.page.backgroundImage === oldUrl) {
                  updatedSlot.page = {
                    ...slot.page,
                    backgroundImage: newUrl
                  };
                  slotUpdated = true;
                }
                
                return slotUpdated ? updatedSlot : slot;
              });
              if (updatedPage.slots !== page.slots) {
                pageUpdated = true;
              }
            }
            
            // ページの横スライド画像
            if (page.horizontalSlides && Array.isArray(page.horizontalSlides)) {
              updatedPage.horizontalSlides = page.horizontalSlides.map((slide: any) => {
                let slideUpdated = false;
                const updatedSlide = { ...slide };
                
                if (slide.backgroundImage === oldUrl) {
                  updatedSlide.backgroundImage = newUrl;
                  slideUpdated = true;
                }
                
                // 横スライド内のページオブジェクトの背景画像
                if (slide.page && slide.page.backgroundImage === oldUrl) {
                  updatedSlide.page = {
                    ...slide.page,
                    backgroundImage: newUrl
                  };
                  slideUpdated = true;
                }
                
                return slideUpdated ? updatedSlide : slide;
              });
              if (updatedPage.horizontalSlides !== page.horizontalSlides) {
                pageUpdated = true;
              }
            }
            
            if (pageUpdated) {
              hasUpdates = true;
              return updatedPage;
            }
            return page;
          });
          if (hasUpdates) {
            updates.pages = updatedPages;
          }
        }
        
        // 設定の更新（ロゴ、ファビコン、背景画像、CTAボタン）
        if (editState?.settings) {
          const updatedSettings: any = { ...editState.settings };
          let settingsUpdated = false;
          
          // ロゴ設定
          if (editState.settings.logo === oldUrl) {
            updatedSettings.logo = newUrl;
            settingsUpdated = true;
          }
          
          // ファビコン設定
          if (editState.settings.favicon === oldUrl) {
            updatedSettings.favicon = newUrl;
            settingsUpdated = true;
          }
          
          // ページ設定の背景画像（PCビュー）
          if (editState.settings.backgroundImage === oldUrl) {
            updatedSettings.backgroundImage = newUrl;
            settingsUpdated = true;
          }
          
          // CTAボタンの画像
          if (editState.settings.ctaButtons && Array.isArray(editState.settings.ctaButtons)) {
            updatedSettings.ctaButtons = editState.settings.ctaButtons.map((cta: any) => {
              let ctaUpdated = false;
              const updatedCta = { ...cta };
              
              if (cta.image === oldUrl) {
                updatedCta.image = newUrl;
                ctaUpdated = true;
              }
              
              if (cta.hoverImage === oldUrl) {
                updatedCta.hoverImage = newUrl;
                ctaUpdated = true;
              }
              
              return ctaUpdated ? updatedCta : cta;
            });
            if (updatedSettings.ctaButtons !== editState.settings.ctaButtons) {
              settingsUpdated = true;
            }
          }
          
          if (settingsUpdated) {
            updates.settings = updatedSettings;
            hasUpdates = true;
          }
        }
        
        // 会社情報の画像
        if (editState?.companyInfo) {
          const updatedCompanyInfo: any = { ...editState.companyInfo };
          let companyInfoUpdated = false;
          
          Object.keys(editState.companyInfo).forEach((key: string) => {
            const value = editState.companyInfo[key];
            if (typeof value === 'string' && value.startsWith('http') && value === oldUrl) {
              updatedCompanyInfo[key] = newUrl;
              companyInfoUpdated = true;
            }
          });
          
          if (companyInfoUpdated) {
            updates.companyInfo = updatedCompanyInfo;
            hasUpdates = true;
          }
        }
        
        // 更新がある場合のみ実行
        if (hasUpdates) {
          updateEditState(updates);
        }
      }
      
      handleEditCancel();
      showSnackbar('ファイル名を変更しました', 'success');
    } catch (error) {
      console.error('ファイル名の変更に失敗しました:', error);
      showSnackbar('ファイル名の変更に失敗しました', 'error');
    }
  };

  // 削除ボタンクリック時（モーダルを開く）
  const handleDeleteClick = (image: ImageMetadata) => {
    setDeletingImage(image);
  };

  // 画像を削除（実際の削除処理）
  const handleDeleteConfirm = async () => {
    if (!deletingImage) return;

    const imageUrl = deletingImage.url;
    const name = deletingImage.name;
    
    try {
      // Firebase Storageから削除
      await ImageService.deleteImage(imageUrl);
      
      // ライブラリの状態から削除
      const updatedImages = images.filter(img => img.url !== imageUrl);
      setImages(updatedImages);
      if (onImagesChange) {
        onImagesChange(updatedImages);
      }
      
      // この画像を使用している設定をクリア
      if (updateEditState) {
        const updates: any = {};
        
        // ページの背景画像をチェック
        if (editState?.pages) {
          const updatedPages = editState.pages.filter((page: any) => 
            page.backgroundImage !== imageUrl
          );
          if (updatedPages.length !== editState.pages.length) {
            updates.pages = updatedPages;
          }
        }
        
        // ロゴ設定をチェック
        if (editState?.settings?.logo === imageUrl) {
          updates.settings = {
            ...editState.settings,
            logo: undefined
          };
        }
        
        // ファビコン設定をチェック
        if (editState?.settings?.favicon === imageUrl) {
          updates.settings = {
            ...editState.settings,
            favicon: undefined
          };
        }
        
        // ロゴとファビコンの両方が同じ画像の場合
        if (editState?.settings?.logo === imageUrl && editState?.settings?.favicon === imageUrl) {
          updates.settings = {
            ...editState.settings,
            logo: undefined,
            favicon: undefined
          };
        }
        
        // 更新がある場合のみ実行
        if (Object.keys(updates).length > 0) {
          updateEditState(updates);
        }
      }
      
      setDeletingImage(null);
      showSnackbar('画像を削除しました', 'success');
    } catch (error) {
      console.error('削除エラー:', error);
      showSnackbar('画像の削除に失敗しました', 'error');
    }
  };

  // ドラッグ&ドロップ処理
  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleDrop = async (event: React.DragEvent) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files).filter(file => 
      ImageService.isSupportedMediaType(file)
    );
    
    if (files.length === 0) return;

    const filesToProcess: Array<{ file: File; fileName: string; isVideo: boolean }> = [];
    
    // 各ファイルのチェックとファイル名決定
    for (const file of files) {
      if (!ImageService.isSupportedMediaType(file)) {
        continue;
      }
      if (!ImageService.isValidFileSize(file, 100)) {
        continue;
      }

      const isVideo = ImageService.isSupportedVideoType(file);
      
      // ファイル拡張子を決定
      let fileExtension: string;
      if (isVideo) {
        fileExtension = 'mp4';
      } else if (file.type === 'image/gif') {
        fileExtension = 'gif';
      } else {
        fileExtension = 'webp';
      }
      
      const originalFileName = file.name.replace(/\.[^/.]+$/, `.${fileExtension}`);
      
      // 同名ファイルのチェック
      const fileExists = await ImageService.fileExists(originalFileName, siteId, isVideo);
      
      if (fileExists) {
        // 同名ファイルがある場合は自動採番
        const availableFileName = await ImageService.getAvailableFileName(originalFileName, siteId, isVideo);
        filesToProcess.push({ file, fileName: availableFileName, isVideo });
      } else {
        // 同名ファイルがない場合はそのまま追加
        filesToProcess.push({ file, fileName: originalFileName, isVideo });
      }
    }
    
    // ファイルを処理
    if (filesToProcess.length > 0) {
      await processFiles(filesToProcess);
    }
  };

  // 使用中の画像URLを集約
  const usedImageUrls = useMemo((): Set<string> => {
    const usedUrls = new Set<string>();
    
    if (!editState) return usedUrls;
    
    // ページの背景画像
    if (editState.pages && Array.isArray(editState.pages)) {
      editState.pages.forEach((page: any) => {
        // ページの背景画像
        if (page.backgroundImage) {
          usedUrls.add(page.backgroundImage);
        }
        // ページスロットの画像
        if (page.slots && Array.isArray(page.slots)) {
          page.slots.forEach((slot: any) => {
            if (slot.imageUrl) {
              usedUrls.add(slot.imageUrl);
            }
            // スロット内のページオブジェクトの背景画像（もしあれば）
            if (slot.page && slot.page.backgroundImage) {
              usedUrls.add(slot.page.backgroundImage);
            }
          });
        }
        // ページの横スライド画像
        if (page.horizontalSlides && Array.isArray(page.horizontalSlides)) {
          page.horizontalSlides.forEach((slide: any) => {
            if (slide.backgroundImage) {
              usedUrls.add(slide.backgroundImage);
            }
            // 横スライド内のページオブジェクトの背景画像（もしあれば）
            if (slide.page && slide.page.backgroundImage) {
              usedUrls.add(slide.page.backgroundImage);
            }
          });
        }
      });
    }
    
    // CTAボタンの画像
    if (editState.settings && editState.settings.ctaButtons && Array.isArray(editState.settings.ctaButtons)) {
      editState.settings.ctaButtons.forEach((cta: any) => {
        if (cta.image) {
          usedUrls.add(cta.image);
        }
        if (cta.hoverImage) {
          usedUrls.add(cta.hoverImage);
        }
      });
    }
    
    // 設定のロゴ・ファビコン・背景画像
    if (editState.settings) {
      if (editState.settings.logo) {
        usedUrls.add(editState.settings.logo);
      }
      if (editState.settings.favicon) {
        usedUrls.add(editState.settings.favicon);
      }
      // ページ設定の背景画像（PCビュー）
      if (editState.settings.backgroundImage) {
        usedUrls.add(editState.settings.backgroundImage);
      }
    }
    
    // 会社情報の画像（もしあれば）
    if (editState.companyInfo) {
      // 会社情報の画像フィールドがある場合に対応
      // 例: editState.companyInfo.logo, editState.companyInfo.image など
      Object.values(editState.companyInfo).forEach((value: any) => {
        if (typeof value === 'string' && value.startsWith('http')) {
          usedUrls.add(value);
        }
      });
    }
    
    return usedUrls;
  }, [editState]);

  // フィルタリングされた画像・動画を取得
  const filteredImages = useMemo(() => {
    return images.filter(image => {
    // タブによるフィルタリング
    if (activeTab === 'images' && image.isVideo) return false;
    if (activeTab === 'videos' && !image.isVideo) return false;
    
    // 検索クエリによるフィルタリング
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      if (!image.name.toLowerCase().includes(query)) {
        return false;
      }
    }
    
    // 使用状況によるフィルタリング
    if (filterType !== 'all') {
        const isUsed = usedImageUrls.has(image.url);
      
      if (filterType === 'used' && !isUsed) return false;
      if (filterType === 'unused' && isUsed) return false;
    }
    
    return true;
  });
  }, [images, activeTab, searchQuery, filterType, usedImageUrls]);

  // ページネーション設定
  const imagesPerPage = 10; // 横5枚 × 縦2枚 = 10枚
  const totalPages = Math.ceil(filteredImages.length / imagesPerPage);
  const startIndex = (currentPage - 1) * imagesPerPage;
  const endIndex = startIndex + imagesPerPage;
  const currentImages = filteredImages.slice(startIndex, endIndex);

  // ページ変更時の処理
  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  // タブ変更時の処理
  const handleTabChange = (event: React.SyntheticEvent, newValue: 'images' | 'videos') => {
    setActiveTab(newValue);
    setCurrentPage(1); // タブ変更時は最初のページに戻す
  };

  // 検索クエリ変更時の処理
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1); // 検索時は最初のページに戻す
  };

  // フィルター表示
  const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  // フィルター閉じる
  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  // フィルター変更
  const handleFilterChange = (type: 'all' | 'used' | 'unused') => {
    setFilterType(type);
    setCurrentPage(1); // フィルター変更時は最初のページに戻す
    setFilterAnchorEl(null);
  };

  // フィルター表示テキスト
  const getFilterLabel = () => {
    switch (filterType) {
      case 'used':
        return '使用中';
      case 'unused':
        return '未使用';
      default:
        return 'すべて';
    }
  };

  // ファイルサイズをフォーマット
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // 日付をフォーマット
  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  return (
    <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,video/mp4,video/avi,video/mov,video/wmv,video/webm"
        multiple
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {/* 検索バーとフィルター */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          placeholder="ファイル名で検索"
          value={searchQuery}
          onChange={handleSearchChange}
          sx={{
            width: '466px',
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#E7E7E7',
              borderRadius: '24px',
              height: '60px',
              fontSize: '18px',
              '& fieldset': {
                border: 'none',
              },
              '& input': {
                padding: '0 16px',
                '&::placeholder': {
                  color: '#666666',
                  opacity: 1,
                },
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#666666', fontSize: '18px' }} />
              </InputAdornment>
            ),
          }}
        />
        <Button
          onClick={handleFilterClick}
          sx={{
            backgroundColor: '#E7E7E7',
            borderRadius: '24px',
            height: '60px',
            minWidth: '120px',
            color: '#000000',
            fontSize: '18px',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#D0D0D0',
            },
          }}
          endIcon={<KeyboardArrowDownIcon />}
        >
          {getFilterLabel()}
        </Button>
        <Menu
          anchorEl={filterAnchorEl}
          open={Boolean(filterAnchorEl)}
          onClose={handleFilterClose}
        >
          <MenuItem onClick={() => handleFilterChange('all')}>すべて</MenuItem>
          <MenuItem onClick={() => handleFilterChange('used')}>使用中</MenuItem>
          <MenuItem onClick={() => handleFilterChange('unused')}>未使用</MenuItem>
        </Menu>
      </Box>

      {/* タブ */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="メディアタブ">
          <Tab 
            label={`画像 (${images.filter(img => !img.isVideo).length})`} 
            value="images" 
          />
          <Tab 
            label={`動画 (${images.filter(img => img.isVideo).length})`} 
            value="videos" 
          />
        </Tabs>
      </Box>

      {/* メディア一覧 */}
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            読み込み中...
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, 320px)',
            gap: 2,
          }}
        >
          {/* アップロードカード（常に左上） */}
          <Card
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            sx={{
              border: '2px dashed #D0D0D0',
              borderWidth: '2px',
              borderStyle: 'dashed',
              borderColor: '#D0D0D0',
              borderRadius: '12px',
              backgroundColor: '#F0F0F0',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '320px',
              height: '360px',
              boxShadow: 'none',
              outline: 'none',
              transition: 'background-color 0.2s',
              '&:hover': {
                backgroundColor: '#E0E0E0',
              }
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 2 }}>
              <ImageIcon sx={{ fontSize: '2.5rem', color: '#999999', mb: 1 }} />
              <Typography variant="body2" sx={{ color: '#666666', textAlign: 'center', fontSize: '14px' }}>
                クリックして画像・動画を選択<br />
                またはドラッグ&ドロップしてアップロード
              </Typography>
              {uploading && <LinearProgress sx={{ mt: 2, width: '100%' }} />}
            </CardContent>
          </Card>

          {/* メディアカード */}
          {currentImages.map((image) => (
            <Card
              key={image.url}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                border: '1px solid #E0E0E0',
                borderRadius: '12px',
                backgroundColor: '#FFFFFF',
                boxShadow: 'none',
                width: '320px',
                height: '360px',
              }}
            >
              <CardContent sx={{ flexGrow: 1, p: 2, position: 'relative' }}>
                {/* サムネイルエリア */}
                <Box
                  sx={{
                    width: '100%',
                    height: '200px',
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#F5F5F5',
                    borderRadius: '8px',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {image.isVideo ? (
                    <>
                      <CardMedia
                        component="video"
                        src={image.url}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                        }}
                        controls={false}
                        muted
                        preload="metadata"
                        crossOrigin="anonymous"
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          borderRadius: '50%',
                          width: 48,
                          height: 48,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                        }}
                        onClick={() => setPreviewImage(image)}
                      >
                        <PlayIcon sx={{ color: 'white', fontSize: 24 }} />
                      </Box>
                    </>
                  ) : (
                    <CardMedia
                      component="img"
                      image={image.url}
                      alt={image.name}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  )}
                  
                  {/* アクションボタン（左右に配置） */}
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyUrl(image.url, image.name);
                    }}
                    sx={{
                      position: 'absolute',
                      left: 8,
                      bottom: 8,
                      backgroundColor: '#9E9E9E',
                      borderRadius: '8px',
                      padding: '8px',
                      color: '#FFFFFF',
                      '&:hover': {
                        backgroundColor: '#757575',
                      }
                    }}
                  >
                    <CopyIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(image);
                    }}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      bottom: 8,
                      backgroundColor: '#9E9E9E',
                      borderRadius: '8px',
                      padding: '8px',
                      color: '#FFFFFF',
                      '&:hover': {
                        backgroundColor: '#757575',
                      }
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Box>

                {/* ファイル情報 */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {editingImage?.url === image.url ? (
                      <TextField
                        value={editImageName}
                        onChange={(e) => setEditImageName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleRenameConfirm();
                          } else if (e.key === 'Escape') {
                            e.preventDefault();
                            handleEditCancel();
                          }
                        }}
                        onBlur={() => {
                          if (editImageName.trim() && editImageName.trim() !== getFileNameWithoutExtension(image.name)) {
                            handleRenameConfirm();
                          } else {
                            handleEditCancel();
                          }
                        }}
                        autoFocus
                        size="small"
                        sx={{
                          flex: 1,
                          '& .MuiOutlinedInput-root': {
                            fontSize: '16px',
                            fontWeight: 500,
                            '& fieldset': {
                              borderColor: '#1B76D2',
                            },
                          },
                        }}
                      />
                    ) : (
                      <>
                        <Tooltip title={image.name}>
                          <Typography 
                            variant="body2" 
                            noWrap 
                            onClick={() => handleEditStart(image)}
                            sx={{ 
                              fontWeight: 500, 
                              fontSize: '16px', 
                              flex: 1,
                              cursor: 'pointer',
                              '&:hover': {
                                textDecoration: 'underline',
                              }
                            }}
                          >
                            {image.name}
                          </Typography>
                        </Tooltip>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditStart(image);
                          }}
                          sx={{
                            padding: '4px',
                            color: '#666666',
                          }}
                        >
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </>
                    )}
                  </Box>
                  <Typography variant="caption" sx={{ fontSize: '14px', color: '#666666' }}>
                    {formatDate(image.uploadedAt)}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '14px', color: '#666666' }}>
                    {formatFileSize(image.size)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* ページネーション */}
      {!loading && totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* プレビューダイアログ */}
      <Dialog
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{previewImage?.name}</Typography>
            <Chip label={previewImage ? formatFileSize(previewImage.size) : ''} size="small" />
          </Box>
        </DialogTitle>
        <DialogContent>
          {previewImage && (
            <>
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                {previewImage.isVideo ? (
                  <video
                    src={previewImage.url}
                    controls
                    crossOrigin="anonymous"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '60vh',
                      objectFit: 'contain',
                    }}
                    onError={(e) => {
                      const videoElement = e.target as HTMLVideoElement;
                      videoElement.style.display = 'none';
                      // エラーメッセージを表示
                      const errorDiv = document.createElement('div');
                      errorDiv.textContent = '動画の再生に失敗しました';
                      errorDiv.style.cssText = 'color: red; text-align: center; padding: 20px;';
                      videoElement.parentNode?.insertBefore(errorDiv, videoElement);
                    }}
                  />
                ) : (
                  <img
                    src={previewImage.url}
                    alt={previewImage.name}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '60vh',
                      objectFit: 'contain',
                    }}
                  />
                )}
              </Box>
              <Box sx={{ p: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>URL:</strong>
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    wordBreak: 'break-all', 
                    mb: 2, 
                    fontFamily: 'monospace',
                    backgroundColor: 'white',
                    p: 1,
                    borderRadius: 1,
                    border: '1px solid #ccc'
                  }}
                >
                  {previewImage.url}
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<CopyIcon />}
                  onClick={() => handleCopyUrl(previewImage.url, previewImage.name)}
                  fullWidth
                >
                  URLをコピー
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewImage(null)}>閉じる</Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認モーダル */}
      <ConfirmDialog
        open={!!deletingImage}
        onClose={() => setDeletingImage(null)}
        onConfirm={handleDeleteConfirm}
        message="削除します。よろしいですか？"
        confirmText="削除"
        confirmColor="#DB2323"
        confirmHoverColor="#C01F1F"
      />

      {/* 同名ファイル確認モーダル */}
      <Dialog
        open={duplicateFileDialog.open}
        onClose={() => setDuplicateFileDialog({ open: false, file: null, fileName: '', isVideo: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">同名ファイルが存在します</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            「{duplicateFileDialog.fileName}」という名前のファイルが既に存在します。
          </Typography>
          <Typography variant="body2" color="text.secondary">
            どのように処理しますか？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateFileDialog({ open: false, file: null, fileName: '', isVideo: false })}>
            キャンセル
          </Button>
          <Button onClick={handleDuplicateAutoNumber}>
            採番して保存（{duplicateFileDialog.fileName.replace(/\.[^/.]+$/, '')}_1{duplicateFileDialog.fileName.match(/\.[^/.]+$/)?.[0] || ''}）
          </Button>
          <Button onClick={handleDuplicateOverwrite} color="error" variant="contained">
            上書き保存
          </Button>
        </DialogActions>
      </Dialog>


      {/* スナックバー */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
